import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AudioInitializer from './components/AudioInitializer';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import WordOfTheDay from './pages/WordOfTheDay';
import Flashcards from './pages/Flashcards';
import Quiz from './pages/Quiz';
import Progress from './pages/Progress';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import Pathways from './pages/Pathways';
import PathwayDetail from './pages/PathwayDetail';
import Users from './pages/Users';
import Cultural from './pages/Cultural';
import TehranLessons from './pages/TehranLessons';
import TehranLessonDetail from './pages/TehranLessonDetail';
import HolidayPacks from './pages/HolidayPacks';
import HolidayPackDetail from './pages/HolidayPackDetail';
import NotFound from './pages/NotFound';
import RecipesPage from './pages/RecipesPage';
import RecipeDetailPage from './pages/RecipeDetailPage';
import ContactUs from './pages/ContactUs';
import HafezLessons from './pages/HafezLessons';
import HafezLesson from './pages/HafezLesson';
import HafezFlashcards from './pages/HafezFlashcards';
import ThreeMinutePersianLesson from './pages/ThreeMinutePersianLesson';
import RichContentLesson from './pages/RichContentLesson';
import ErrorBoundary from './components/ErrorBoundary';
import AudioDebugTool from './components/AudioDebugTool';
import MobileAudioDemo from './components/MobileAudioDemo';
import TermsOfService from './pages/TermsOfService';
import Forum from './pages/Forum';
import AskQuestion from './pages/AskQuestion';
import QuestionDetail from './pages/QuestionDetail';
import HelpCenter from './pages/HelpCenter';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import About from './pages/About';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import PremiumBenefits from './pages/PremiumBenefits';
import LearningGuide from './pages/LearningGuide';
import BecomeTeacher from './pages/BecomeTeacher';
import VajakLessons from './pages/VajakLessons';
import VajakLesson from './pages/VajakLesson';
import ProverbFlashcards from './pages/ProverbFlashcards';
import VajakFlashcards from './pages/VajakFlashcards';
import PersianIdioms from './pages/PersianIdioms';
import TarofTips from './pages/TarofTips';
import Blog from './pages/Blog';
import SuccessStories from './pages/SuccessStories';
import Events from './pages/Events';
import Contact from './pages/Contact';
import Carreers from './pages/Carreers';
import BlogArticle from './pages/BlogArticle';
import CookiePolicy from './pages/CookiePolicy';
import PersianCompanionTraveller from './pages/PersianCompanionTraveller';
import TravelGuide from './pages/TravelGuide';
import FoodGuide from './pages/FoodGuide';
import StayGuide from './pages/StayGuide';
import ShopGuide from './pages/ShopGuide';
import CultureGuide from './pages/CultureGuide';
import SocialGuide from './pages/SocialGuide';
import PhrasesGuide from './pages/PhrasesGuide';
import AdventureGuide from './pages/AdventureGuide';
import Vajak from './pages/Vajak';
import AiBotChat from './pages/AiBotChat';
import VocabularyGarden from './pages/VocabularyGarden';
import VocabularyCategory from './pages/VocabularyCategory';
import FavoritesPage from './pages/FavoritesPage';
import MyGarden from './pages/MyGarden';
import SearchResults from './pages/SearchResults';
import AlphabetLearningPathway from './pages/AlphabetLearningPathway';
import GridAlphabetLesson from './pages/GridAlphabetLesson';
import AlphabetGrid from './pages/AlphabetGrid';
import TeacherQA from './pages/TeacherQA';
import LearnFarsiFast from './pages/LearnFarsiFast';
import PersianForTravel from './pages/PersianForTravel';
import IsPersianHardToLearn from './pages/IsPersianHardToLearn';
import FarsiVsPersian from './pages/FarsiVsPersian';
import PersianAlphabetGuide from './pages/PersianAlphabetGuide';
import VocabularyBuilder from './pages/VocabularyBuilder';
import Teachers from './pages/Teachers';
import TeacherProfile from './pages/TeacherProfile';
import BookSession from './pages/BookSession';
import TeacherSessionPayment from './pages/TeacherSessionPayment';
import SessionSuccess from './pages/SessionSuccess';
import SessionManagement from './pages/SessionManagement';
import Feedback from './pages/Feedback';
import Comparison from './pages/Comparison';
import Resources from './pages/Resources';
import EasyPersianAlphabetRedirect from './components/EasyPersianAlphabetRedirect';
import TalkingAboutYourFamilyRedirect from './components/TalkingAboutYourFamilyRedirect';
import SharedLearningPage from './pages/SharedLearningPage';
import PersianLiteraturePage from './pages/PersianLiteraturePage';

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <AudioInitializer>
            <Router>
              <Layout>
                <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/word-of-the-day" element={
                <ProtectedRoute>
                  <WordOfTheDay />
                </ProtectedRoute>
              } />
              <Route path="/flashcards" element={
                <ProtectedRoute>
                  <Flashcards />
                </ProtectedRoute>
              } />
              <Route path="/quiz" element={
                <ProtectedRoute>
                  <Quiz />
                </ProtectedRoute>
              } />
              <Route path="/progress" element={
                <ProtectedRoute>
                  <Progress />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              } />
              <Route path="/pathways" element={<Pathways />} />
              
              {/* Specific pathway routes - must come before generic /pathways/:id */}
              <Route path="/pathways/alphabet-learning-pathway/lesson/:lessonId" element={<GridAlphabetLesson />} />
              <Route path="/pathways/persian-politeness-masterclass-ep1/lesson-rich/taarof-ep1-lesson-1" element={
                <ErrorBoundary>
                  <RichContentLesson />
                </ErrorBoundary>
              } />


              <Route path="/pathways/:pathwayId/lesson/:lessonId" element={
                <ProtectedRoute>
                  <ThreeMinutePersianLesson />
                </ProtectedRoute>
              } />
              <Route path="/pathways/:pathwayId/rich-lesson/:lessonId" element={
                <ErrorBoundary>
                  <RichContentLesson />
                </ErrorBoundary>
              } />
              
              {/* Generic pathway route - must come after specific routes */}
              <Route path="/pathways/:id" element={<PathwayDetail />} />
              <Route path="/alphabet-learning" element={<AlphabetLearningPathway />} />
              <Route path="/easy-persian-alphabet" element={<EasyPersianAlphabetRedirect />} />
              <Route path="/talking-about-your-family" element={<TalkingAboutYourFamilyRedirect />} />
              <Route path="/alphabet-grid" element={<AlphabetGrid />} />
              <Route path="/alphabet-grid/:letterId" element={<GridAlphabetLesson />} />
              <Route path="/users" element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="/cultural" element={
                <ProtectedRoute>
                  <Cultural />
                </ProtectedRoute>
              } />
              <Route path="/cultural/tehran" element={
                <ProtectedRoute>
                  <TehranLessons />
                </ProtectedRoute>
              } />
              <Route path="/cultural/tehran/:id" element={
                <ProtectedRoute>
                  <TehranLessonDetail />
                </ProtectedRoute>
              } />
              <Route path="/cultural/holidays" element={<HolidayPacks />} />
              <Route path="/cultural/holidays/:id" element={<HolidayPackDetail />} />
              <Route path="/cultural/recipes" element={
                <ProtectedRoute>
                  <RecipesPage />
                </ProtectedRoute>
              } />
              <Route path="/cultural/recipes/:id" element={
                <ProtectedRoute>
                  <RecipeDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/hafez" element={
                <ProtectedRoute>
                  <HafezLessons />
                </ProtectedRoute>
              } />
              <Route path="/hafez/lesson/:lessonId" element={
                <ProtectedRoute>
                  <HafezLesson />
                </ProtectedRoute>
              } />
              <Route path="/hafez/flashcards/:lessonId" element={
                <ProtectedRoute>
                  <HafezFlashcards />
                </ProtectedRoute>
              } />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/about" element={<About />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/premium-benefits" element={<PremiumBenefits />} />
              <Route path="/ask-share" element={<Forum />} />
              <Route path="/unlock-all" element={<Subscription />} />
              <Route path="/learning-guide" element={<LearningGuide />} />
              <Route path="/forum" element={<Forum />} />
              <Route path="/forum/ask" element={
                <ProtectedRoute>
                  <AskQuestion />
                </ProtectedRoute>
              } />
              <Route path="/forum/question/:id" element={<QuestionDetail />} />
              <Route path="/forum/category/:category" element={<Forum />} />
              <Route path="/teacher-qa" element={<TeacherQA />} />
              <Route path="/vocabulary" element={<VocabularyBuilder />} />
              <Route path="/teachers" element={<Teachers />} />
              <Route path="/teacher/:teacherId" element={<TeacherProfile />} />
              <Route path="/book-session/:teacherId" element={
                <ProtectedRoute>
                  <BookSession />
                </ProtectedRoute>
              } />
              <Route path="/teacher-session-payment/:teacherId" element={
                <ProtectedRoute>
                  <TeacherSessionPayment />
                </ProtectedRoute>
              } />
              <Route path="/session-success/:sessionId" element={
                <ProtectedRoute>
                  <SessionSuccess />
                </ProtectedRoute>
              } />
              <Route path="/my-sessions" element={
                <ProtectedRoute>
                  <SessionManagement />
                </ProtectedRoute>
              } />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/shared-learning" element={<SharedLearningPage />} />
              <Route path="/shared-learning/:id" element={<SharedLearningPage />} />
              <Route path="/become-teacher" element={<BecomeTeacher />} />
              <Route path="/vajak" element={<VajakLessons />} />
              <Route path="/vajak/lesson/:lessonId" element={<VajakLesson />} />
              <Route path="/vajak/flashcards/:lessonId" element={
                <ProtectedRoute>
                  <VajakFlashcards />
                </ProtectedRoute>
              } />
              <Route path="/vajak-space" element={<Vajak />} />
              <Route path="/proverb-flashcards" element={<ProverbFlashcards />} />
              <Route path="/idioms" element={<PersianIdioms />} />
              <Route path="/tarof-tips" element={<TarofTips />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/success-stories" element={<SuccessStories />} />
              <Route path="/events" element={<Events />} />
              <Route path="/carreers" element={<Carreers />} />
              <Route path="/travel-companion" element={<PersianCompanionTraveller />} />
              <Route path="/travel-guide" element={<TravelGuide />} />
              <Route path="/food-guide" element={<FoodGuide />} />
              <Route path="/stay-guide" element={<StayGuide />} />
              <Route path="/shop-guide" element={<ShopGuide />} />
              <Route path="/culture-guide" element={<CultureGuide />} />
              <Route path="/social-guide" element={<SocialGuide />} />
              <Route path="/phrases-guide" element={<PhrasesGuide />} />
              <Route path="/adventure-guide" element={<AdventureGuide />} />
              <Route path="/vocabulary-garden" element={<VocabularyGarden />} />
              <Route path="/vocabulary/:category" element={<VocabularyCategory />} />
              <Route path="/favorites" element={<FavoritesPage />} />
              <Route path="/my-garden" element={<MyGarden />} />
              <Route path="/ai-chat" element={<AiBotChat />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/learn-farsi-fast" element={<LearnFarsiFast />} />
              <Route path="/persian-for-travel" element={<PersianForTravel />} />
              <Route path="/is-persian-hard-to-learn" element={<IsPersianHardToLearn />} />
              <Route path="/farsi-vs-persian" element={<FarsiVsPersian />} />
              <Route path="/persian-alphabet-guide" element={<PersianAlphabetGuide />} />
              <Route path="/persian-literature-demo" element={<PersianLiteraturePage />} />
              
              {/* Development/Debug routes */}
              {process.env.NODE_ENV === 'development' && (
                <>
                  <Route path="/debug/audio" element={<AudioDebugTool />} />
                  <Route path="/debug/mobile-audio" element={<MobileAudioDemo />} />
                </>
              )}
              
              <Route path="*" element={<NotFound />} />
                          </Routes>
            </Layout>
          </Router>
          </AudioInitializer>
        </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
};

export default App; 